// TUT-450 — o painel e a mesa: widgets, série e medição, visões salvas e os quadros Kanban salvos.
import { demoApi } from "../demo-data";
import type {
  KanbanBoard,
  KanbanBoardListResponse,
  SaveKanbanBoardInput,
  UpdateKanbanBoardInput,
  DashboardData,
  MesaResponse,
  RecorteDaMesa,
  DashboardMeasurementResponse,
  EntregasResponse,
  DashboardSeriesResponse,
  DashboardView,
  DashboardViewListResponse,
  CreateDashboardViewInput,
  UpdateDashboardViewInput,
} from "../types";
import type {
  DashboardMeasurementMedicao,
  DashboardSeriesPeriod,
  DashboardSeriesSource,
  DashboardSeriesAgrupamento,
  DashboardWidgetId,
} from "../dashboardLayout";
import { request } from "./infra";

// Lido aqui, e não importado: o empacotador só dobra a constante no módulo
// que a escreve, e é isso que tira o demo do build de produção (TUT-448).
const useDemoApi = import.meta.env.VITE_API_MODE === "demo";

export const apiDoPainel = {
  // `janelaDias` ausente = o servidor herda `prazos.janela_aviso_dias` do
  // escritório. Vai como parâmetro OPCIONAL, e não como valor resolvido aqui:
  // a tela não conhece o ajuste do escritório e não deve reimplementá-lo.
  dashboard(
    widgets: DashboardWidgetId[],
    janelaDias: number | null = null,
  ): Promise<DashboardData> {
    if (useDemoApi) return demoApi.dashboard(widgets, janelaDias);
    const params = new URLSearchParams({ widgets: widgets.join(",") });
    if (janelaDias !== null) params.set("janelaDias", String(janelaDias));
    return request<DashboardData>(`/dashboard/widgets?${params}`);
  },
  /** TUT-313b — a lista de um cartão da Minha mesa (a conta do número dele). */
  mesa(recorte: RecorteDaMesa): Promise<MesaResponse> {
    if (useDemoApi) return demoApi.mesa(recorte);
    return request<MesaResponse>(
      `/dashboard/desk?${new URLSearchParams({ recorte })}`,
    );
  },
  // TUT-203h — a Serie temporal, client-fetched como a Linha do processo: nao
  // passa pela porta comum de widgets (`staticDashboardWidgetIds` a exclui).
  // TUT-214d — `agrupamento` e OPCIONAL (default "tempo" no servidor): so
  // vai na query quando alguem escolheu outra coisa, no mesmo espirito de
  // `janelaDias` em `dashboard()` acima.
  dashboardSeries(
    fonte: DashboardSeriesSource,
    periodo: DashboardSeriesPeriod,
    agrupamento: DashboardSeriesAgrupamento = "tempo",
  ): Promise<DashboardSeriesResponse> {
    if (useDemoApi) return demoApi.dashboardSeries(fonte, periodo, agrupamento);
    const params = new URLSearchParams({ fonte, periodo });
    if (agrupamento !== "tempo") params.set("agrupamento", agrupamento);
    return request<DashboardSeriesResponse>(`/dashboard/series?${params}`);
  },
  // TUT-203i — a Medicao e meta, client-fetched como a Serie temporal: nao
  // passa pela porta comum de widgets (`staticDashboardWidgetIds` a exclui).
  dashboardMeasurement(
    medicao: DashboardMeasurementMedicao,
  ): Promise<DashboardMeasurementResponse> {
    if (useDemoApi) return demoApi.dashboardMeasurement(medicao);
    const params = new URLSearchParams({ medicao });
    return request<DashboardMeasurementResponse>(
      `/dashboard/measurement?${params}`,
    );
  },
  /**
   * TUT-578 (D4) — as entregas do período (peça entregue, laudo protocolado):
   * a lista "Entregues" do Agendamento, pela mesma porta da Medição.
   */
  dashboardEntregas(periodoDe: string, periodoAte: string): Promise<EntregasResponse> {
    if (useDemoApi) return demoApi.dashboardEntregas(periodoDe, periodoAte);
    return request<EntregasResponse>(`/dashboard/entregas?${new URLSearchParams({ periodoDe, periodoAte })}`);
  },
  dashboardViews(): Promise<DashboardViewListResponse> {
    return useDemoApi
      ? demoApi.dashboardViews()
      : request<DashboardViewListResponse>("/dashboard/views");
  },
  createDashboardView(input: CreateDashboardViewInput): Promise<DashboardView> {
    return useDemoApi
      ? demoApi.createDashboardView(input)
      : request<DashboardView>("/dashboard/views", {
          method: "POST",
          body: JSON.stringify(input),
        });
  },
  updateDashboardView(
    id: string,
    input: UpdateDashboardViewInput,
  ): Promise<DashboardView> {
    return useDemoApi
      ? demoApi.updateDashboardView(id, input)
      : request<DashboardView>(`/dashboard/views/${encodeURIComponent(id)}`, {
          method: "PATCH",
          body: JSON.stringify(input),
        });
  },
  deleteDashboardView(id: string): Promise<void> {
    return useDemoApi
      ? demoApi.deleteDashboardView(id)
      : request<void>(`/dashboard/views/${encodeURIComponent(id)}`, {
          method: "DELETE",
        });
  },
  kanbanBoards(): Promise<KanbanBoardListResponse> {
    if (useDemoApi) return demoApi.kanbanBoards();
    return request<KanbanBoardListResponse>("/kanban/boards");
  },
  createKanbanBoard(input: SaveKanbanBoardInput): Promise<KanbanBoard> {
    if (useDemoApi) return demoApi.createKanbanBoard(input);
    return request<KanbanBoard>("/kanban/boards", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  updateKanbanBoard(
    id: string,
    input: UpdateKanbanBoardInput,
  ): Promise<KanbanBoard> {
    if (useDemoApi) return demoApi.updateKanbanBoard(id, input);
    return request<KanbanBoard>(`/kanban/boards/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    });
  },
  deleteKanbanBoard(id: string): Promise<void> {
    if (useDemoApi) return demoApi.deleteKanbanBoard(id);
    return request<void>(`/kanban/boards/${encodeURIComponent(id)}`, {
      method: "DELETE",
    });
  },
};
